package com.schoolconnect.app.feature.common

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import androidx.compose.ui.platform.LocalDensity
import com.schoolconnect.app.repository.school.SchoolRepository
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.search
import org.jetbrains.compose.resources.painterResource
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.distinctUntilChanged

data class SchoolLookupOption(val id: String, val name: String)

data class SchoolLookupUiState(
    val query: String = "",
    val options: List<SchoolLookupOption> = emptyList(),
    val isLoading: Boolean = false,
    val isLoadingMore: Boolean = false,
    val hasMore: Boolean = true,
    val error: String = ""
)

class SchoolLookupViewModel(
    private val repository: SchoolRepository = SchoolRepository
) {
    companion object { const val PAGE_SIZE = 7 }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val searchDebouncer = DebouncedActionRunner(scope)
    private var requestJob: Job? = null
    private val _uiState = MutableStateFlow(SchoolLookupUiState(isLoading = true))
    val uiState: StateFlow<SchoolLookupUiState> = _uiState.asStateFlow()

    init { loadPage(reset = true) }

    fun onQueryChanged(value: String) {
        _uiState.update { it.copy(query = value, error = "", isLoading = true, isLoadingMore = false) }
        searchDebouncer.submit { loadPage(reset = true) }
    }

    fun loadMore() {
        val state = _uiState.value
        if (state.isLoading || state.isLoadingMore || !state.hasMore) return
        loadPage(reset = false)
    }

    private fun loadPage(reset: Boolean) {
        requestJob?.cancel()
        requestJob = scope.launch {
            val current = _uiState.value
            val pageIndex = if (reset) 0 else current.options.size / PAGE_SIZE
            _uiState.update {
                it.copy(
                    isLoading = reset,
                    isLoadingMore = !reset,
                    error = "",
                    options = if (reset) emptyList() else it.options
                )
            }
            repository.loadSchoolsPage(
                query = current.query,
                isActive = true,
                pageIndex = pageIndex,
                pageSize = PAGE_SIZE
            ).fold(
                onSuccess = { page ->
                    val mapped = page.mapNotNull { school ->
                        val id = school.id.orEmpty().trim()
                        val name = school.name.orEmpty().trim()
                        if (id.isBlank() || name.isBlank()) null else SchoolLookupOption(id, name)
                    }
                    val total = page.serverTotalCountOrNull()
                    _uiState.update { state ->
                        val next = if (reset) mapped else (state.options + mapped).distinctBy { it.id }
                        state.copy(
                            options = next,
                            isLoading = false,
                            isLoadingMore = false,
                            hasMore = total?.let { next.size < it } ?: (mapped.size == PAGE_SIZE)
                        )
                    }
                },
                onFailure = { error ->
                    _uiState.update { it.copy(isLoading = false, isLoadingMore = false, error = error.message ?: "Unable to load schools") }
                }
            )
        }
    }

    fun clear() {
        searchDebouncer.cancel()
        requestJob?.cancel()
        scope.cancel()
    }
}

@Composable
fun ApiSchoolDropdown(
    selectedLabel: String,
    onSelected: (SchoolLookupOption) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String = "Search and select school",
    error: String = ""
) {
    val viewModel = remember { SchoolLookupViewModel() }
    val state by viewModel.uiState.collectAsState()
    var expanded by remember { mutableStateOf(false) }
    var anchorSize by remember { mutableStateOf(IntSize.Zero) }
    val listState = rememberLazyListState()
    val popupWidth = with(LocalDensity.current) { anchorSize.width.toDp() }

    DisposableEffect(viewModel) { onDispose { viewModel.clear() } }
    LaunchedEffect(expanded, listState) {
        if (expanded) {
            snapshotFlow { listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: -1 }
                .distinctUntilChanged()
                .collectLatest { lastVisible ->
                    val current = viewModel.uiState.value
                    if (current.hasMore && current.options.isNotEmpty() && lastVisible >= current.options.lastIndex - 1) {
                        viewModel.loadMore()
                    }
                }
        }
    }

    Column(modifier = modifier) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(40.dp)
                .onGloballyPositioned { anchorSize = it.size }
                .border(1.dp, if (expanded) Color(0xFF2563EB) else Color(0xFFE5E7EB), RoundedCornerShape(8.dp))
                .background(Color.White, RoundedCornerShape(8.dp))
                .clickable { expanded = !expanded }
                .padding(horizontal = 12.dp),
            contentAlignment = Alignment.CenterStart
        ) {
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Text(
                    selectedLabel.ifBlank { placeholder },
                    color = if (selectedLabel.isBlank()) Color(0xFF536080) else Color(0xFF111827),
                    fontSize = 14.sp,
                    fontWeight = FontWeight.Medium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f)
                )
                Icon(painterResource(Res.drawable.chevron_down), contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(20.dp))
            }
        }
        if (expanded) {
            Popup(
                popupPositionProvider = schoolDropdownPopupPositionProvider(),
                properties = PopupProperties(
                    focusable = true,
                    dismissOnBackPress = true,
                    dismissOnClickOutside = true
                ),
                onDismissRequest = { expanded = false }
            ) {
                Surface(
                    modifier = Modifier.width(popupWidth),
                    shape = RoundedCornerShape(8.dp),
                    color = Color.White,
                    shadowElevation = 8.dp
                ) {
                    Column {
                        Row(Modifier.fillMaxWidth().height(40.dp).padding(horizontal = 12.dp), verticalAlignment = Alignment.CenterVertically) {
                            Icon(painterResource(Res.drawable.search), contentDescription = null, tint = Color(0xFF64739D), modifier = Modifier.size(16.dp))
                            Spacer(Modifier.size(8.dp))
                            BasicTextField(value = state.query, onValueChange = viewModel::onQueryChanged, singleLine = true, textStyle = TextStyle(fontSize = 14.sp, color = Color(0xFF111827)), cursorBrush = SolidColor(Color(0xFF2563EB)), modifier = Modifier.fillMaxWidth()) { inner ->
                                Box(Modifier.fillMaxWidth()) { if (state.query.isBlank()) Text("Search school...", color = Color(0xFF536080), fontSize = 14.sp, fontWeight = FontWeight.Medium); inner() }
                            }
                        }
                        if (state.isLoading && state.options.isEmpty()) {
                            Box(Modifier.fillMaxWidth().height(100.dp), contentAlignment = Alignment.Center) { CircularProgressIndicator(modifier = Modifier.size(22.dp), strokeWidth = 2.dp) }
                        } else if (state.error.isNotBlank() && state.options.isEmpty()) {
                            Text(state.error, color = Color(0xFFB42318), modifier = Modifier.padding(18.dp))
                        } else if (state.options.isEmpty()) {
                            Text("No schools found", color = Color(0xFF536080), fontSize = 14.sp, fontWeight = FontWeight.Medium, modifier = Modifier.padding(18.dp))
                        } else {
                            LazyColumn(state = listState, modifier = Modifier.fillMaxWidth().heightIn(max = 235.dp)) {
                                items(state.options, key = { it.id }) { option ->
                                    Box(Modifier.fillMaxWidth().clickable { onSelected(option); expanded = false }.padding(horizontal = 12.dp, vertical = 10.dp)) {
                                        Text(option.name, color = Color(0xFF111827), fontSize = 14.sp, fontWeight = FontWeight.Medium, maxLines = 1, overflow = TextOverflow.Ellipsis)
                                    }
                                }
                                if (state.isLoadingMore) {
                                    item {
                                        Box(Modifier.fillMaxWidth().padding(10.dp), contentAlignment = Alignment.Center) {
                                            CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        if (error.isNotBlank()) Text(error, color = Color(0xFFB42318), fontSize = 12.sp, modifier = Modifier.padding(top = 6.dp))
    }
}

private fun schoolDropdownPopupPositionProvider(): PopupPositionProvider {
    return object : PopupPositionProvider {
        override fun calculatePosition(
            anchorBounds: IntRect,
            windowSize: IntSize,
            layoutDirection: androidx.compose.ui.unit.LayoutDirection,
            popupContentSize: IntSize
        ): IntOffset {
            val x = anchorBounds.left.coerceIn(0, (windowSize.width - popupContentSize.width).coerceAtLeast(0))
            val belowY = anchorBounds.bottom + 8
            val aboveY = anchorBounds.top - popupContentSize.height - 8
            val y = if (belowY + popupContentSize.height <= windowSize.height) belowY else aboveY.coerceAtLeast(0)
            return IntOffset(x, y)
        }
    }
}
